import React from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'

import { toast } from 'react-toastify'
import { useFormContext } from 'react-hook-form'
import { Box, useTheme } from '@mui/material'

import { get } from 'utils/lodash'
import { handleError } from 'utils/error'
import { formatDateToBEFormat } from 'utils/date'
import { formatDollarsToCents, formatDollarsToDecimalCents } from 'utils/price'
import { useCreateWorkOrdersDisposalTicketMutation } from 'api/work-order/createWorkOrdersDisposalTicket'
import { useUpdateWorkOrdersDisposalTicketMutation } from 'api/work-order/updateWorkOrdersDisposalTicket'
import { useDeleteWorkOrdersDisposalTicketMutation } from 'api/work-order/deleteWorkOrdersDisposalTicket'

import T from 'T'
import HHDialogActions from 'components/common/HHDialogActions'
import CancelButton from 'components/buttons/CancelButton'
import SaveButton from 'components/buttons/SaveButton'
import BillingLockedTooltip from 'components/billing/common/lock-billing-date/BillingLockedTooltip'
import HhConfirmDeleteButton from 'components/common/HHConfirmDeleteButton'

const DialogFooter = ({ isLocked = false, workOrderId, onSuccess = noop, onClose = noop }) => {
  const theme = useTheme()
  const [createWorkOrdersDisposalTicket, { isLoading: isCreateLoading }] = useCreateWorkOrdersDisposalTicketMutation()
  const [updateWorkOrdersDisposalTicket, { isLoading: isUpdateLoading }] = useUpdateWorkOrdersDisposalTicketMutation()
  const [deleteWorkOrdersDisposalTicket, { isLoading: isDeleteLoading }] = useDeleteWorkOrdersDisposalTicketMutation()
  const {
    watch,
    handleSubmit,
    formState: { isDirty },
  } = useFormContext()
  const disposalTicketId = watch('disposalTicketId')
  const taxable = watch('taxable')

  const handleDelete = () => {
    deleteWorkOrdersDisposalTicket({ workOrderId, disposalTicketId }).unwrap().then(onSuccess).catch(handleError).finally(onClose)
  }

  const handleSaveError = () => {
    toast.error(T.REQUIRED_FIELD_MSG)
  }

  const handleSave = data => {
    if (!isDirty) {
      onClose()
      return
    }

    const { effectiveDate, ticketNumber, disposalSiteId, materials } = data
    const materialsPayload = materials.map(material => {
      return {
        id: get(material, 'id'),
        disposalSiteMaterialId: get(material, 'disposalSiteMaterialId'),
        unitOfMeasure: get(material, 'unitOfMeasure'),
        quantity: get(material, 'quantity', 0),
        unitPriceCents: formatDollarsToCents(get(material, 'unitPriceDollars', 0)),
        feeMaterialId: get(material, 'feeMaterialId'),
        useSiteRate: !get(material, 'useDisposalFeeRate'),
        rate: formatDollarsToDecimalCents(get(material, 'rate')),
        includedTonnage: get(material, 'includedTonnage'),
        useMinimumCharge: get(material, 'useMinimumCharge'),
        minimumChargeCents: formatDollarsToCents(get(material, 'minimumCharge')),
        overageWeight: get(material, 'overageWeight'),
        overageFeeCents: formatDollarsToCents(get(material, 'overageFee')),
      }
    })

    const body = {
      ...(effectiveDate && { effectiveDate: formatDateToBEFormat(effectiveDate) }),
      ticketNumber,
      disposalSiteId,
      taxable,
      materials: materialsPayload.concat(get(data, 'toBeDeletedMaterials', [])),
    }

    if (disposalTicketId) {
      updateWorkOrdersDisposalTicket({ workOrderId, disposalTicketId, body }).unwrap().then(onSuccess).catch(handleError).finally(onClose)
      return
    }
    createWorkOrdersDisposalTicket({ workOrderId, body }).unwrap().then(onSuccess).catch(handleError).finally(onClose)
  }

  return (
    <HHDialogActions>
      {disposalTicketId && (
        <BillingLockedTooltip isLocked={isLocked} placement="bottom">
          <HhConfirmDeleteButton
            loading={isDeleteLoading}
            disabled={isLocked || isCreateLoading || isUpdateLoading}
            confirmTitle="Delete disposal ticket"
            confirmDescription="Are you sure you want to delete this disposal ticket?"
            DialogProps={{ sx: { zIndex: theme.zIndex.tooltip + 20 } }}
            onDeleteConfirmed={handleDelete}
          />
        </BillingLockedTooltip>
      )}
      <Box flex={1} />
      <CancelButton onClick={onClose} />
      <BillingLockedTooltip isLocked={isLocked} placement="bottom">
        <SaveButton
          disabled={isLocked || isDeleteLoading}
          loading={isCreateLoading || isUpdateLoading}
          onClick={handleSubmit(handleSave, handleSaveError)}
        />
      </BillingLockedTooltip>
    </HHDialogActions>
  )
}

DialogFooter.propTypes = {
  isLocked: PropTypes.bool,
  workOrderId: PropTypes.string.isRequired,
  onSuccess: PropTypes.func,
  onClose: PropTypes.func,
}

export default DialogFooter
